import React, {ReactNode, useCallback, useContext, useEffect, useState} from "react";
import {MfaMethod, SignInAttempt} from "../../../types";
import axios from "axios";

interface SignInContextValue {
    signInAttempt?: SignInAttempt;
    submitCredentials: (username: string, password: string, rememberMe: boolean, sessionId: string, ip: string) => Promise<void>;
    setMfaMethod: (mfaMethod: MfaMethod) => Promise<void>;
    attemptMfaVerification: (mfaSecret: string) => Promise<void>;
    submitSsoCode: (loginId: string, ssoCode: string, clientId: string, sessionId: string, ip: string, redirectUri: string) => Promise<void>;
    checkInIAMHub: (loginid: string) => Promise<any>;
    loginId?: string;
    error?: string;
}

const defaultValue = {
    submitCredentials: async () => {},
    setMfaMethod: async () => {},
    attemptMfaVerification: async () => {},
    submitSsoCode: async () => {},
    checkInIAMHub: async () => {},
};

const SignInContext = React.createContext<SignInContextValue>(defaultValue)

// This resume shape (minted per-ceremony by the OTA adapter) is issued both
// for live Ping-first ceremonies (internal/CSR hosts, Folio-Cloudfront) and
// as a stale bookmark that would otherwise bounce the user back for a second
// login -- the URL shape alone can't tell them apart, and document.referrer
// is unreliable here (empty even on a genuine PingFederate redirect). The
// double-login UX issue this guards against only matters for real users in
// UAT/PROD; DEV/QA need every resume forwarded so local dev and API testing
// against a live PingFederate ceremony keep working. The SAML resume shape
// (/idp/{id}/resumeSAML20/idp/startSSO.ping) is a live SSO bridge and is
// intentionally left untouched.
const STALE_OAUTH_RESUME_PATTERN = /^\/as\/[a-zA-Z0-9]+\/resume\/as\/authorization\.ping$/;
const TRUSTED_PING_ORIGINS = ["https://idfs-qa.gs.com", "https://idfs.gs.com"];

function isTrustedUrl(url: string): boolean {
    try {
        return TRUSTED_PING_ORIGINS.includes(new URL(url).origin);
    } catch {
        return false;
    }
}

function isDevOrQaEnvironment(): boolean {
    const domain = window.location.hostname;
    return domain.includes('dev.') || domain.includes('qa.') || domain.includes('localhost');
}

function dropIfStaleOauthResume(resume: string | null): string | null {
    if (isDevOrQaEnvironment()) {
        return resume;
    }
    return resume && STALE_OAUTH_RESUME_PATTERN.test(resume) ? null : resume;
}

interface SignInContextProviderProps {
    children: ReactNode
}

export function SignInContextProvider({children}: SignInContextProviderProps) {

    const [signInAttempt, setSignInAttempt] = useState<SignInAttempt>();
    const [error, setError] = useState<string>();
    const folioHostProd = "https://folioinstitutional.com/"
    const folioHostUat = "https://uat.folioinstitutional.com/"


    useEffect(() => {
        if (error) {
            setTimeout(() => setError(undefined), 5000);
        }
    }, [error])

    const submitCredentials = useCallback(async (username: string, password: string, rememberMe: boolean, sessionId: string, ip: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const resume = dropIfStaleOauthResume(searchParams.get('resume'));
        const next = searchParams.get('next');

        let url = `/api/v2/sign-in-attempts`;
        if (resume) {
            url += `?resume=${resume}`;
        }
        if (next) {
            url += `${resume ? '&' : '?'}next=${encodeURIComponent(next)}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({loginId: username, password, rememberMe, sessionId, ip}),
        });

        if (response.ok) {
            const body = await response.json();
            sessionStorage.setItem('isLocked', 'false');
            setSignInAttempt(body);
            if (body.reviewStatus == "pass") {
                const pingFederateUrl = new URL(body.redirectUrl);
                const params = Object.fromEntries(pingFederateUrl.searchParams.entries());
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = pingFederateUrl.origin + pingFederateUrl.pathname;
                for (const [key, value] of Object.entries(params)) {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                }
                document.body.appendChild(form);
                form.submit();
            }
        } else if (response.status === 504) {
            setError('The server failed to respond');
            throw new Error();
        } else if (response.status === 403) {
            const body = await response.json();
            if (body.displayMessage.includes("locked")){
                sessionStorage.setItem('isLocked', 'true');
                window.location.href = "/sign-in/forgot-password"
            } else {
                sessionStorage.setItem('isLocked', 'false');
                setError("These log in credentials do not match our records. Your account will be locked after three " +
                    "unsuccessful attempts and you will be asked to reset your password.")
            }
        } else if (response.status >= 400 && response.status < 500) {
            const body = await response.json();
            setError(body.displayMessage);
            if (body.displayMessage.includes("restricted")){
                if (window.location.hostname === "advisorsolutions.gs.com" || window.location.hostname === "alts-advisorsolutions.gs.com" ) {
                    window.location.href = folioHostProd;
                } else {
                    window.location.href = folioHostUat;
                }
            }
        } else if (response.status >= 500) {
            setError('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setError(body.displayMessage);
            throw new Error();
        }
    }, [setSignInAttempt]);

    const setMfaMethod = useCallback(async (mfaMethod: MfaMethod) => {
        if (!signInAttempt) {
            return;
        }
        const response = await fetch(`/api/v2/sign-in-attempts/${signInAttempt?.signInAttemptId}/mfa-method`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(mfaMethod),
        });

        if (response.ok) {
            setSignInAttempt({...signInAttempt, mfaMethod});
        } else if (response.status === 504) {
            setError('The server failed to respond');
            throw new Error();
        } else if (response.status >= 400 && response.status < 500) {
            const body = await response.json();
            setError(body.displayMessage);
        } else if (response.status >= 500) {
            setError('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setError(body.displayMessage);
            throw new Error();
        }
    }, [signInAttempt]);

    const attemptMfaVerification = useCallback(async (mfaSecret: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const resume = dropIfStaleOauthResume(searchParams.get('resume'));
        const next = searchParams.get('next');

        let url = `/api/v2/sign-in-attempts/${signInAttempt?.signInAttemptId}/mfa-verifications`;
        if (resume) {
            url += `?resume=${resume}`;
        }
        if (next) {
            url += `${resume ? '&' : '?'}next=${encodeURIComponent(next)}`;
        }

        const response = await fetch(
            url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({mfaSecret}),
            });

        if (response.ok) {
            const body = await response.json();
            const {redirectUrl} = body;
            const pingFederateUrl = new URL(redirectUrl);
            const params = Object.fromEntries(pingFederateUrl.searchParams.entries());
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = pingFederateUrl.origin + pingFederateUrl.pathname;
            for (const [key, value] of Object.entries(params)) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                form.appendChild(input);
            }
            document.body.appendChild(form);
            form.submit();
        } else if (response.status === 504) {
            setError('The server failed to respond');
            throw new Error();
        } else if (response.status >= 400 && response.status < 500) {
            const body = await response.json();
            setError(body.displayMessage);
        } else if (response.status >= 500) {
            setError('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setError(body.displayMessage);
            throw new Error();
        }
    }, [signInAttempt?.signInAttemptId]);

        function sanitizeInput(input: string): string {
            return input
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#x27;");
        }


        function delay(ms: number): Promise<void> {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        const checkInIAMHub = useCallback(async (loginid: string) => {
            return axios.get(`/api/v2/sign-in-attempts/${encodeURIComponent(loginid)}/iam-migration-status`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                withCredentials: true,
            });
        }, []);


        const submitSsoCode = useCallback(async (loginId: string, ssoCode: string, clientId: string, sessionId: string, ip: string, redirectUri: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const resume = searchParams.get('resume');

        let url = `/api/v2/sign-in-attempts/sso-code`;
        if (resume) {
            url += `?resume=${resume}`;
        }

        await delay(7000);
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({loginId, ssoCode, clientId, sessionId, ip, redirectUri}),
        });

        if (response.ok) {
            const body = await response.json();
            setSignInAttempt(body);
            if (body.reviewStatus == "pass" && isTrustedUrl(body.redirectUrl)) {
                const pingFederateUrl = new URL(body.redirectUrl);
                const params = Object.fromEntries(pingFederateUrl.searchParams.entries());
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = pingFederateUrl.origin + pingFederateUrl.pathname;
                for (let [key, value] of Object.entries(params)) {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    key = key.replace(/[&#+()$~%'"*<>{}]/g, '');
                    value = value.replace(/[&#+()$~%'"*<>{}]/g, '');
                    input.name = sanitizeInput(key);
                    input.value = sanitizeInput(value);
                    form.appendChild(input);
                }
                document.body.appendChild(form);
                form.submit();
            } 
        } else if (response.status === 504) {
            setError('The server failed to respond');
            throw new Error();
        } else if (response.status >= 400 && response.status < 500) {
            setError("There is an issue completing this SSO")
        } else if (response.status >= 500) {
            setError('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setError(body.displayMessage);
            throw new Error();
        }
    }, [setSignInAttempt]);

    return (
        <SignInContext.Provider value={{signInAttempt, submitCredentials, setMfaMethod, attemptMfaVerification, submitSsoCode, checkInIAMHub, error}}>
            {children}
        </SignInContext.Provider>
    );
}

export const useSignInContext = () => useContext<SignInContextValue>(SignInContext);